import { checkClientIntent, handleApiError } from './authGuards';
import { openModal } from './navigationController';
import { showToast } from './toastController';
import { PrototypeApiError } from './apiTransport';
import { getRecoverableTurnstileToken, isTurnstileClientError } from './turnstileRuntime';
import type { PrototypeState } from './controllerState';
import type { Root } from './prototypeTypes';
import { syncCommunityIdeaVoteCache } from './communityMountState';
import { paintCommunityVoteAllowance } from './communityVoteAllowance';

const locks = new Set<string>();
interface VoteCooldown {
	timer: ReturnType<typeof setTimeout>;
	elapsed: boolean;
	settled: boolean;
}
const cooldowns = new WeakMap<Root, Map<string, VoteCooldown>>();
const VOTE_COOLDOWN_MS = 2_000;

export function openVotePaywall(root: Root, state: PrototypeState): boolean {
	const decision = checkClientIntent(state, 'voteIdea');
	if (decision.allowed) return false;
	if (decision.reason === 'login') {
		openModal(root, 'loginModal');
		return true;
	}
	return true;
}

export function isVotable(source: HTMLElement): boolean {
	const item = source.closest<HTMLElement>('.idea-item, .community-detail');
	return item?.dataset.status === 'voting' && item.dataset.isAdopted !== 'true';
}

function feedback(source: HTMLElement): void {
	source.classList.remove('community-support-burst');
	void source.offsetWidth;
	source.classList.add('community-support-burst');
	window.setTimeout(() => source.classList.remove('community-support-burst'), 650);
}

function updateVotePresentation(root: Root, ideaId: string, liked: boolean): void {
	root.querySelectorAll<HTMLElement>('[data-idea-id]').forEach((item) => {
		if (item.dataset.ideaId !== ideaId) return;
		item.dataset.viewerLiked = String(liked);
		item.querySelectorAll<HTMLElement>('.vote-btn, .idea-detail-vote-btn').forEach((button) => {
			button.classList.toggle('voted', liked);
			button.setAttribute('aria-label', liked ? 'You supported this request' : 'Support this request');
		});
	});
}

function cooldownsFor(root: Root): Map<string, VoteCooldown> {
	const existing = cooldowns.get(root);
	if (existing) return existing;
	const created = new Map<string, VoteCooldown>();
	cooldowns.set(root, created);
	return created;
}

function releaseVoteCooldown(root: Root, ideaId: string): void {
	cooldownsFor(root).delete(ideaId);
	root.querySelectorAll<HTMLElement>('[data-idea-id]').forEach((item) => {
		if (item.dataset.ideaId !== ideaId || !isVotable(item)) return;
		item.querySelectorAll<HTMLButtonElement>('.vote-btn, .idea-detail-vote-btn').forEach((button) => {
			delete button.dataset.communityVoteCooldown;
			button.disabled = false;
			button.removeAttribute('aria-disabled');
		});
	});
}

function setVoteCooldown(root: Root, ideaId: string): void {
	const byIdea = cooldownsFor(root);
	const existing = byIdea.get(ideaId);
	if (existing) clearTimeout(existing.timer);
	root.querySelectorAll<HTMLElement>('[data-idea-id]').forEach((item) => {
		if (item.dataset.ideaId !== ideaId) return;
		item.querySelectorAll<HTMLButtonElement>('.vote-btn, .idea-detail-vote-btn').forEach((button) => {
			button.dataset.communityVoteCooldown = 'true';
			button.disabled = true;
			button.setAttribute('aria-disabled', 'true');
		});
	});
	const cooldown = { timer: undefined as unknown as ReturnType<typeof setTimeout>, elapsed: false, settled: false };
	cooldown.timer = setTimeout(() => {
		cooldown.elapsed = true;
		if (cooldown.settled) releaseVoteCooldown(root, ideaId);
	}, VOTE_COOLDOWN_MS);
	byIdea.set(ideaId, cooldown);
}

function settleVoteCooldown(root: Root, ideaId: string): void {
	const cooldown = cooldownsFor(root).get(ideaId);
	if (!cooldown) return;
	cooldown.settled = true;
	if (cooldown.elapsed) releaseVoteCooldown(root, ideaId);
}

export async function submitCommunityVote(
	root: Root,
	state: PrototypeState,
	source: HTMLElement,
	_voteValue: 1 | -1 = 1,
): Promise<void> {
	const ideaId = source.dataset.ideaId ?? source.closest<HTMLElement>('[data-idea-id]')?.dataset.ideaId ?? '';
	const item = source.closest<HTMLElement>('.idea-item, .community-detail');
	if (!ideaId || !item || locks.has(ideaId) || cooldownsFor(root).has(ideaId)) return;
	if (!isVotable(source)) {
		showToast({ type: 'info', message: 'Voting has ended' });
		return;
	}

	const wasLiked = item.dataset.viewerLiked === 'true';
	const nextLiked = !wasLiked;
	updateVotePresentation(root, ideaId, nextLiked);
	syncCommunityIdeaVoteCache(root, ideaId, { viewerLiked: nextLiked });
	if (nextLiked) feedback(source);
	else source.classList.remove('community-support-burst');
	setVoteCooldown(root, ideaId);
	locks.add(ideaId);
	source.setAttribute('aria-busy', 'true');

	try {
		const turnstileToken = await getRecoverableTurnstileToken(state.runtime, 'community-idea-vote');
		const response = await state.runtime.api.voteCommunityIdea(ideaId, { liked: nextLiked, turnstileToken });
		const result = response as {
			viewerLiked?: boolean;
			viewerVoteAllowance?: { limit: number; remaining: number; resetAt: string };
		};
		if (result.viewerLiked !== nextLiked) throw new Error('community_vote_not_recorded');

		updateVotePresentation(root, ideaId, Boolean(result.viewerLiked));
		syncCommunityIdeaVoteCache(root, ideaId, { viewerLiked: Boolean(result.viewerLiked) });
		paintCommunityVoteAllowance(root, {
			isLoggedIn: true,
			allowance: result.viewerVoteAllowance,
		});
	} catch (error) {
		updateVotePresentation(root, ideaId, wasLiked);
		syncCommunityIdeaVoteCache(root, ideaId, { viewerLiked: wasLiked });
		source.classList.remove('community-support-burst');
		if (error instanceof PrototypeApiError && error.status === 409) {
			showToast({ type: 'info', message: 'Voting has ended' });
		} else if (error instanceof PrototypeApiError && error.status === 403 && (error.body as { error?: string }).error === 'daily_vote_limit_reached') {
			paintCommunityVoteAllowance(root, {
				isLoggedIn: true,
				allowance: (error.body as { viewerVoteAllowance?: { limit: number; remaining: number; resetAt: string } }).viewerVoteAllowance,
			});
			showToast({ type: 'info', message: 'Your 3 supports for today are used' });
		} else if (isTurnstileClientError(error)) {
			showToast({ type: 'info', message: 'Verification failed. Please try again.' });
		} else {
			const handled = handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: 'vote' });
			if (!handled) showToast({ type: 'error', message: 'Supporting is temporarily unavailable. Please try again later.' });
		}
	} finally {
		locks.delete(ideaId);
		settleVoteCooldown(root, ideaId);
		source.removeAttribute('aria-busy');
	}
}
